<template>
  <CCard>
    <CCardHeader>
      <h2 class="text-uppercase font-weight-bold mb-0">Firmware Management</h2>
    </CCardHeader>

    <CCardBody>
      <div v-if="initialLoading" class="text-center py-5">
        <CSpinner color="primary" style="width: 3rem; height: 3rem" />
        <h5 class="mt-3 text-muted font-weight-normal">Loading firmwares...</h5>
      </div>

      <div v-else>
        <div class="d-flex flex-wrap align-items-end mb-4 border p-3 rounded control-box">
          <div class="flex-grow-1" style="min-width: 200px; max-width: 400px">
            <label for="firmware-select" class="font-weight-bold">Selected Firmware</label>
            <select
              id="firmware-select"
              v-model="selectedFirmwareId"
              class="form-control"
              :disabled="isProcessing"
              @change="onFirmwareChange"
            >
              <option :value="null" disabled>-- Choose a Firmware --</option>
              <option v-for="fw in firmwares" :key="fw.id" :value="fw.id">
                #{{ fw.id }} - {{ fw.name }}
              </option>
            </select>
          </div>

          <CButton
            color="success"
            class="mr-2"
            style="height: 38px"
            :disabled="isProcessing"
            @click="openCreateModal"
          >
            <i v-if="isProcessing" class="fas fa-spinner fa-spin mr-1" />
            <i v-else class="fas fa-plus-circle mr-1" />
            <span>New</span>
          </CButton>

          <CButton
            v-if="selectedFirmwareId"
            color="danger"
            style="height: 38px"
            :disabled="isProcessing"
            @click="confirmDeleteFirmware"
          >
            <i v-if="isProcessing" class="fas fa-spinner fa-spin mr-1" />
            <span v-else>Delete</span>
          </CButton>
        </div>

        <div v-if="selectedFirmwareId">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="mb-0">
              OTA Files
              <span v-if="selectedFirmwareName" class="text-muted font-weight-normal">
                - #{{ selectedFirmwareId }} - {{ selectedFirmwareName }}
              </span>
            </h4>
            <CButton color="primary" :disabled="isProcessing" @click="newFile">
              <i v-if="isProcessing" class="fas fa-spinner fa-spin mr-1" />
              <i v-else class="fas fa-upload mr-1" />
              Upload New File
            </CButton>
          </div>

          <CDataTable
            :items="data"
            :fields="c_fields"
            :pagination="false"
            :sorter="{ external: true }"
            :loading="loading || isProcessing"
            class="mb-0"
            :no-items-view="{ noItems: 'No files found' }"
            @update:sorter-value="sortingChange"
          >
            <template #status="data">
              <td>
                <span
                  class="badge"
                  :class="data.item.status === 'completed' ? 'badge-success' : 'badge-warning'"
                >
                  {{ data.item.status.toUpperCase() }}
                </span>
              </td>
            </template>

            <template #is_published="data">
              <td class="text-center">
                <span v-if="data.item.is_published" class="badge badge-success px-2 py-1">
                  <i class="fas fa-check-circle mr-1" />
                  Published
                </span>
                <span v-else class="text-muted">-</span>
              </td>
            </template>

            <template #created_at="data">
              <td>
                {{ $formatDateTime(data.item.created_at) }}
              </td>
            </template>

            <template #actions="data">
              <td class="text-right">
                <div class="button-group">
                  <CButton
                    v-if="data.item.status === 'completed' && !data.item.is_published"
                    size="sm"
                    color="success"
                    class="action-button"
                    :disabled="isProcessing"
                    @click="publishFile(data.item.id)"
                  >
                    Publish
                  </CButton>

                  <CButton
                    v-if="!data.item.is_published"
                    size="sm"
                    color="danger"
                    class="action-button"
                    :disabled="isProcessing"
                    @click="confirmDeleteFile(data.item.id)"
                  >
                    Delete
                  </CButton>

                  <CButton
                    v-if="data.item.status === 'completed'"
                    size="sm"
                    color="primary"
                    class="action-button"
                    :disabled="isProcessing"
                    @click="downloadFile(data.item.id)"
                  >
                    Download
                  </CButton>
                </div>
              </td>
            </template>
          </CDataTable>

          <div class="d-flex justify-content-end mt-4">
            <pagination
              v-if="options.last_page > 1"
              :current-page="options.page"
              :last-page="options.last_page"
              class="mr-3"
              @paginate="pageChange"
            />
          </div>
        </div>

        <div v-else class="text-center text-muted py-5">
          <h5 class="font-weight-normal">
            Please select or create a firmware to manage its files.
          </h5>
        </div>
      </div>
    </CCardBody>

    <create-firmware-modal ref="create_modal" @saved="executeCreateFirmware" />

    <confirm-action-modal ref="confirm_modal" @confirmed="executeConfirmedAction" />

    <upload-firmware-modal
      ref="upload_modal"
      :firmware-id="selectedFirmwareId"
      @saved="onFirmwareChange"
    />
  </CCard>
</template>

<script>
import UploadFirmwareModal from './UploadFirmwareModal.vue';
import ConfirmActionModal from '@/components/ConfirmActionModal.vue';
import CreateFirmwareModal from './CreateFirmwareModal.vue';
import externalTable from '@/mixins/externalTable';
import Pagination from '@/components/Pagination';

export default {
  name: 'FirmwareManagement',
  components: {
    UploadFirmwareModal,
    ConfirmActionModal,
    CreateFirmwareModal,
    Pagination, // Registrado
  },
  mixins: [externalTable],
  data() {
    return {
      initialLoading: true,
      firmwares: [],
      selectedFirmwareId: null,
      isProcessing: false,
      url: '',
    };
  },
  computed: {
    selectedFirmwareName() {
      if (!this.selectedFirmwareId) return '';
      const fw = this.firmwares.find((f) => f.id === this.selectedFirmwareId);
      return fw ? fw.name : '';
    },
    c_fields() {
      return [
        { key: 'version', label: 'v.' },
        { key: 'original_filename', label: 'Filename' },
        { key: 'status', label: 'Status' },
        { key: 'is_published', label: 'Published', class: 'text-center' },
        { key: 'created_at', label: 'Upload Date' },
        {
          key: 'actions',
          label: 'Actions',
          class: 'text-right',
          sorter: false,
        },
      ];
    },
  },
  mounted() {
    this.fetchFirmwares();
  },
  methods: {
    async fetchFirmwares() {
      try {
        this.initialLoading = true;
        const response = await this.$http.get('/firmwares');
        this.firmwares = response.data;

        if (this.firmwares.length > 0 && !this.selectedFirmwareId) {
          this.selectedFirmwareId = this.firmwares[0].id;
          this.onFirmwareChange();
        }
      } catch (error) {
        console.error('Error fetching firmwares:', error);
      } finally {
        this.initialLoading = false;
      }
    },

    onFirmwareChange() {
      if (!this.selectedFirmwareId) return;
      this.url = `/firmwares/${this.selectedFirmwareId}/files`;
      this.options.page = 1;
      this.fetchData();
    },

    openCreateModal() {
      this.$refs.create_modal.open();
    },

    async executeCreateFirmware(name) {
      try {
        this.isProcessing = true;
        const response = await this.$http.post('/firmwares', { name: name });
        await this.fetchFirmwares();
        this.selectedFirmwareId = response.data.id;
        this.onFirmwareChange();
      } catch (error) {
        console.error('Error creating firmware:', error);
      } finally {
        this.isProcessing = false;
      }
    },

    newFile() {
      if (this.selectedFirmwareId) {
        this.$refs.upload_modal.open();
      }
    },

    confirmDeleteFirmware() {
      this.$refs.confirm_modal.open(
        { action: 'delete_firmware' },
        'Are you sure you want to delete this firmware AND ALL its files in AWS? This cannot be undone.',
      );
    },

    confirmDeleteFile(fileId) {
      this.$refs.confirm_modal.open(
        { action: 'delete_file', id: fileId },
        'Do you really want to delete this OTA file?',
      );
    },

    async executeConfirmedAction(payload) {
      if (!payload) return;

      this.isProcessing = true;

      try {
        if (payload.action === 'delete_firmware') {
          await this.$http.delete(`/firmwares/${this.selectedFirmwareId}`);
          this.selectedFirmwareId = null;
          this.data = [];
          await this.fetchFirmwares();
        } else if (payload.action === 'delete_file') {
          await this.$http.delete(`/firmware-files/${payload.id}`);
          this.fetchData();
        }
      } catch (error) {
        console.error('Error in confirmed action:', error);
      } finally {
        this.isProcessing = false;
      }
    },

    async downloadFile(fileId) {
      try {
        this.isProcessing = true;
        const response = await this.$http.get(`/firmware-files/${fileId}/download`);
        const url =
          response.data?.download_url || response.data?.data?.download_url || response.data?.url;
        if (url) {
          window.open(url, '_blank');
        } else {
          this.$toast.error('Download URL not found');
        }
      } catch (error) {
        this.$toast.error('Error downloading file');
      } finally {
        this.isProcessing = false;
      }
    },

    async publishFile(fileId) {
      try {
        this.isProcessing = true;
        await this.$http.post(`/firmware-files/${fileId}/publish`);
        this.fetchData();
      } catch (error) {
        console.error('Error publishing file:', error);
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style scoped>
.control-box {
  border-color: #d8dbe0 !important;
  background-color: transparent;
}

.control-box > * + * {
  margin-left: 0.5rem;
}

.badge-success {
  background-color: #28a745;
  color: white;
  padding: 0.25em 0.5em;
  border-radius: 0.25em;
  font-size: 0.875em;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
  padding: 0.25em 0.5em;
  border-radius: 0.25em;
  font-size: 0.875em;
}

.button-group {
  display: flex;
  justify-content: flex-end;
}

.action-button {
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
